import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadGuide, toContentLocale } from './loadGuide';
import type { Locale } from './types';

export type GuideContentStatus = 'idle' | 'loading' | 'ready' | 'error';

export function useGuideContent(id: string | null): {
  status: GuideContentStatus;
  markdown: string;
  locale: Locale;
  retry: () => void;
} {
  const { i18n } = useTranslation('HelpGuide');
  const locale = toContentLocale(i18n.language);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{
    status: GuideContentStatus;
    markdown: string;
  }>({
    status: 'idle',
    markdown: '',
  });

  useEffect(() => {
    if (!id) {
      setState({ status: 'idle', markdown: '' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading', markdown: '' });
    loadGuide(locale, id)
      .then((markdown) => !cancelled && setState({ status: 'ready', markdown }))
      .catch(() => !cancelled && setState({ status: 'error', markdown: '' }));
    return () => {
      cancelled = true;
    };
  }, [id, locale, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);
  return { ...state, locale, retry };
}
